"use client";

import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import log from "logbeacon";
import { useOspLoginProvider } from "@/src/features/auth/compat/useOspLoginProvider";
import { useSnapshot } from "valtio";
import { rootStore } from "@store";
import {
  createAiClient,
  createChatClient,
  createMemberClient,
  createNotifyClient,
  createOpenApiClient,
  createOpenDataClient,
  createQuestApiClient,
  createSwapApiClient,
  createTrackerClient,
} from "@/src/features/api/client/factories";

type OpenApiClientType = ReturnType<typeof createOpenApiClient>;
type QuestApiClientType = ReturnType<typeof createQuestApiClient>;
type ChatApiClientType = ReturnType<typeof createChatClient>;
type NotifyApiClientType = ReturnType<typeof createNotifyClient>;
type MemberApiClientType = ReturnType<typeof createMemberClient>;
type OpenDataApiClientType = ReturnType<typeof createOpenDataClient>;
type TrackerApiClientType = ReturnType<typeof createTrackerClient>;
type AiApiClientType = ReturnType<typeof createAiClient>;
type SwapApiClientType = ReturnType<typeof createSwapApiClient>;
export const useCreateOspApi = () => {
  const { auth } = useSnapshot(rootStore.login);
  const { fingerprint } = useSnapshot(rootStore.user);
  const { authModule } = useOspLoginProvider();
  const [_, setVersion] = useState(0);
  const openApiClientRef = useRef<OpenApiClientType | undefined>(undefined);
  const questApiClientRef = useRef<QuestApiClientType | undefined>(undefined);
  const chatApiClientRef = useRef<ChatApiClientType | undefined>(undefined);
  const notifyApiClientRef = useRef<NotifyApiClientType | undefined>(undefined);
  const memberApiClientRef = useRef<MemberApiClientType | undefined>(undefined);
  const openDataApiClientRef = useRef<OpenDataApiClientType | undefined>(
    undefined,
  );
  const aiApiClientRef = useRef<AiApiClientType | undefined>(undefined);
  const swapApiClientRef = useRef<SwapApiClientType | undefined>(undefined);
  const trackerApiClientRef = useRef<TrackerApiClientType | undefined>(
    undefined,
  );

  const updateRef = useCallback(<T>(ref: MutableRefObject<T>, value: T) => {
    if (ref.current !== value) {
      ref.current = value;
      setVersion((prev) => prev + 1);
    }
  }, []);

  const normalizedFingerprint: string | undefined = fingerprint ?? undefined;

  useEffect(() => {
    const swapClient = createSwapApiClient();
    updateRef(swapApiClientRef, swapClient);
    return () => {
      updateRef(swapApiClientRef, undefined);
    };
  }, [updateRef]);

  useEffect(() => {
    // @ts-ignore
    // TODO: fix this
    const client = createAiClient(authModule);
    updateRef(aiApiClientRef, client);
    return () => {
      updateRef(aiApiClientRef, undefined);
    };
  }, [authModule, updateRef]);

  useEffect(() => {
    // @ts-ignore
    // TODO: fix this
    const client = createOpenApiClient(authModule);
    log.debug("[useCreateOspApi] createOpenApiClient", client, authModule);
    updateRef(openApiClientRef, client);
    if (client) {
      rootStore.user.apiClient = client;
    }
    return () => {
      if (rootStore.user.apiClient === client) {
        rootStore.user.apiClient = undefined as any;
      }
      updateRef(openApiClientRef, undefined);
    };
  }, [authModule, updateRef]);

  useEffect(() => {
    // @ts-ignore
    // TODO: fix this
    const client = createQuestApiClient(authModule, auth);
    updateRef(questApiClientRef, client);
    return () => {
      updateRef(questApiClientRef, undefined);
    };
  }, [authModule, auth, updateRef]);

  useEffect(() => {
    // @ts-ignore
    // TODO: fix this
    const client = createChatClient(authModule, normalizedFingerprint);
    updateRef(chatApiClientRef, client);
    return () => {
      updateRef(chatApiClientRef, undefined);
    };
  }, [authModule, normalizedFingerprint, updateRef]);

  useEffect(() => {
    // @ts-ignore
    // TODO: fix this
    const client = createTrackerClient(authModule, normalizedFingerprint);
    updateRef(trackerApiClientRef, client);
    return () => {
      updateRef(trackerApiClientRef, undefined);
    };
  }, [authModule, normalizedFingerprint, updateRef]);

  useEffect(() => {
    // @ts-ignore
    // TODO: fix this
    const client = createNotifyClient(authModule, normalizedFingerprint);
    updateRef(notifyApiClientRef, client);
    return () => {
      updateRef(notifyApiClientRef, undefined);
    };
  }, [authModule, normalizedFingerprint, updateRef]);

  useEffect(() => {
    // @ts-ignore
    // TODO: fix this
    const client = createMemberClient(authModule, normalizedFingerprint);
    updateRef(memberApiClientRef, client);
    return () => {
      updateRef(memberApiClientRef, undefined);
    };
  }, [authModule, normalizedFingerprint, updateRef]);

  useEffect(() => {
    // @ts-ignore
    // TODO: fix this
    const client = createOpenDataClient(authModule, normalizedFingerprint);
    updateRef(openDataApiClientRef, client);
    return () => {
      updateRef(openDataApiClientRef, undefined);
    };
  }, [authModule, normalizedFingerprint, updateRef]);

  return {
    openApiClient: openApiClientRef.current,
    aiApiClient: aiApiClientRef.current,
    questApiClient: questApiClientRef.current,
    chatApiClient: chatApiClientRef.current,
    notifyApiClient: notifyApiClientRef.current,
    swapApiClient: swapApiClientRef.current,
    openDataApiClient: openDataApiClientRef.current,
    memberApiClient: memberApiClientRef.current,
    trackerApiClient: trackerApiClientRef.current,
  };
};
